import { FabricImage, FabricObject, FabricText, Point, StaticCanvas } from 'fabric';
import type { FabricWorld } from '@/commons/fabricjs/FabricWorld';

/**
 * Pass a list of element you want to reorder using zIndex likewise system
 * @param fabricCanvas fabric canvas context
 * @param elementList Array of fabric objects, usually text or image
 * @param order enum 'firstBehind' or 'firstFront' default is 'firstFront' meaning first element in array will be on top
 */
function orderElementList(
    fabricCanvas: StaticCanvas | null,
    elementList: ((FabricImage & { id: string }) | (FabricText & { id: string }))[],
    sortOrderById?: string[],
    order: 'firstBehind' | 'firstFront' = 'firstFront',
) {
    if (!fabricCanvas || elementList.length === 0) {
        return;
    }
    const sortedObject: FabricObject[] = sortOrderById ? [] : elementList;
    if (sortOrderById) {
        sortOrderById.forEach((id) => {
            const found = elementList.find((element) => element.id === id);
            if (found) {
                sortedObject.push(found);
            }
        });
    }
    sortedObject.forEach((element) => {
        if (order === 'firstBehind') {
            // carefull it might be counter intuitive
            // last element in for loop will go forward, first elements will become back because of that
            fabricCanvas?.bringObjectForward(element);
        } else {
            // last element will go backwards, first elements wont move
            fabricCanvas?.sendObjectBackwards(element);
        }
    });
    sortedObject
        .filter((element) => element.type === 'image')
        .forEach((element) => {
            fabricCanvas?.bringObjectToFront(element);
        });
    sortedObject
        .filter((element) => element.type === 'text')
        .forEach((element) => {
            fabricCanvas?.bringObjectToFront(element);
        });
}

/**
 * Center Fabric element in the center of canvas based on canvas size and element size
 * @param fElement Fabric Text or Image
 * @returns void
 */
function canvasCenterElement(fabricWorld: FabricWorld | null, fElement: FabricObject | null) {
    if (!fabricWorld?.fabricCanvas || !fElement) {
        return;
    }
    fElement.set('left', (fabricWorld.canvasWidth - (fElement.width || 0)) / 2);
    fElement.set('top', (fabricWorld.canvasHeight - (fElement.height || 0)) / 2);
    fElement.set('dirty', true);
    fElement.setCoords(); // update handle positions
    fabricWorld.fabricCanvas.renderAll();
}

/**
 * Convert all position type elem
 * Convert all position type elemh, left, top height etc) to take into account a change in canvas size
 * It is usefull to convert a project from a canvas size to another, it helps solving retina or devicePixelRatio issues
 * @param fObject Element you want to convert
 * @param oldCanvasWidth canvas width of project when it was exported
 * @param oldCanvasHeight canvas height of project when it was exported
 */
function convertObjectFromOldPositionToNewPosition(
    fObject: FabricObject | undefined,
    oldCanvasWidth: number | undefined,
    oldCanvasHeight: number | undefined,
    currentCanvasWitdh: number | undefined,
    currentCanvasHeight: number | undefined,
) {
    if (!(fObject instanceof FabricObject) || !oldCanvasWidth || !oldCanvasHeight || !currentCanvasWitdh || !currentCanvasHeight) {
        return;
    }
    const ratioW = currentCanvasWitdh / oldCanvasWidth;
    const ratioH = currentCanvasHeight / oldCanvasHeight;
    if (currentCanvasWitdh !== oldCanvasWidth) {
        fObject.width *= ratioW;
        fObject.left *= ratioW;
        fObject.scaleX *= ratioW;
        fObject.skewX *= ratioW;
    }
    if (currentCanvasHeight !== oldCanvasHeight) {
        fObject.height *= ratioH;
        fObject.top *= ratioH;
        fObject.scaleY *= ratioH;
        fObject.skewY *= ratioH;
    }
}

/**
 *
 * @closure fElement
 * @param fElementUpdated
 * @returns { function } thet take the same element updated and center it
 */
function initCenterPositionFromCenter(fabricCanvas: StaticCanvas, fElement: FabricObject) {
    const coords = fElement.getCoords();
    const oldVec = new Point(coords[1].x - coords[0].x, coords[1].y - coords[0].y);
    // eslint-disable-next-line func-names
    return function (fElementUpdated: FabricObject) {
        const newCoords = fElementUpdated.getCoords();
        const newVec = new Point(newCoords[1].x - newCoords[0].x, newCoords[1].y - newCoords[0].y);
        const decal = newVec.subtract(oldVec);
        fElementUpdated.set('left', fElementUpdated.left - decal.x / 2);
        fElementUpdated.set('top', fElementUpdated.top - decal.y / 2);
        fElementUpdated.set('dirty', true);
        fElementUpdated.setCoords();
    };
}

export { orderElementList, canvasCenterElement, convertObjectFromOldPositionToNewPosition, initCenterPositionFromCenter };
